import { create } from 'zustand';
import { authService } from '@/services/AuthService';
import { parseJwtClaims, getTokenExpiration, validateToken } from '@/utils/jwtUtils';
import { triggerJitProvisioning } from '@/utils/jitProvision';
import { API_CONFIG } from '@/config/api';
import type { AuthState, AuthActions, TokenResponse, User } from '@/types/Auth';
import { logger } from '@equip/utils/logger';
import { filterEquipRoles } from '@equip/utils';
import { telemetry } from '@/utils/telemetry';

/**
 * Thrown when an authenticated identity carries no recognised eQuip application
 * role (user/admin/viewer/frontwindow). The token is otherwise valid, but the
 * user has not been granted access to eQuip, so the session must be rejected.
 */
export class AuthorizationError extends Error {
  constructor(message = 'Your account is not authorized to access eQuip.') {
    super(message);
    this.name = 'AuthorizationError';
  }
}

// Multi-Tab Session Limitation (Code Review Issue #CR1)
// Tokens are stored only in-memory (not in sessionStorage/localStorage).
// This means each browser tab requires separate login, which confuses users.
// Single-tab token refresh uses module-level promise which is not shared across tabs.
// Multiple tabs refreshing simultaneously can cause token revocation.
// Consider: BroadcastChannel API for cross-tab sync or sessionStorage with XSS mitigations.

type AuthStore = AuthState & AuthActions;
type TestWindow = Window & { __TEST_AUTH_TOKEN__?: unknown; useAuthStore?: unknown };

// Shared promise to prevent multiple simultaneous refresh requests
let refreshPromise: Promise<boolean> | null = null;
let refreshTimerId: ReturnType<typeof setTimeout> | null = null;

const scheduleTokenRefresh = (accessToken: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (refreshTimerId) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }

  const expiration = getTokenExpiration(accessToken);
  if (!expiration) {
    return;
  }

  const timeUntilRefresh = expiration.getTime() - Date.now() - API_CONFIG.TOKEN_REFRESH_BUFFER_MS;

  if (timeUntilRefresh <= 0) {
    refreshTimerId = null;
    void useAuthStore.getState().refreshTokens();
    return;
  }

  refreshTimerId = setTimeout(() => {
    refreshTimerId = null;
    void useAuthStore.getState().refreshTokens();
  }, timeUntilRefresh);
};

export const useAuthStore = create<AuthStore>((set, get) => {
  // Initialize state
  let initialState = {
    user: null as User | null,
    accessToken: null as string | null,
    idToken: null as string | null,
    refreshToken: null as string | null,
    isAuthenticated: false,
    isLoading: true, // Start as loading to prevent premature redirects during SSO attempt
  };

  // Check for test auth token on store creation (E2E testing support)
  if (typeof window !== 'undefined') {
    const testToken = (window as TestWindow).__TEST_AUTH_TOKEN__;
    const testMode = sessionStorage.getItem('__test_mode__');

    if (typeof testToken === 'string' && testToken.length > 0 && testMode) {
      try {
        // Inject test tokens synchronously during store creation
        const user = parseJwtClaims(testToken);
        initialState = {
          user,
          accessToken: testToken,
          idToken: testToken,
          refreshToken: 'mock-refresh-token',
          isAuthenticated: true,
          isLoading: false,
        };
        console.log('Test mode: Auth tokens injected during store creation');
      } catch (error) {
        console.error('Test mode: Failed to parse test token', error);
      }
    }
  }

  return {
    // State
    ...initialState,

    // Actions
    login: async () => {
      set({ isLoading: true });
      try {
        // Assuming authService.login handles the redirect.
        // The rest of the login flow will be handled by a callback page.
        await authService.login();
      } catch (error) {
        logger.error('Login failed', { component: 'AuthStore', error: String(error) });
        set({ isLoading: false });
        throw error;
      }
    },

    clearAuth: () => {
      if (refreshTimerId) {
        clearTimeout(refreshTimerId);
        refreshTimerId = null;
      }

      set({
        user: null,
        accessToken: null,
        idToken: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
      });
    },

    setTokens: async (tokens: TokenResponse) => {
      try {
        // Validate token with signature verification (security check)
        await validateToken(tokens.access_token);

        const user = parseJwtClaims(tokens.access_token);

        // Authorization gate: the token may be validly signed yet belong to an
        // identity that was never granted eQuip access (e.g. any member of the
        // Azure tenant). parseJwtClaims surfaces Keycloak default realm roles,
        // so presence of *any* role is not sufficient — require at least one
        // recognised eQuip application role (user/admin/viewer/frontwindow).
        const equipRoles = filterEquipRoles(user.roles);
        if (equipRoles.length === 0) {
          logger.warn('Rejecting login: identity has no eQuip application role', {
            component: 'AuthStore',
            username: user.username,
            roles: user.roles,
          });
          telemetry.track('auth.unauthorized.no_role', { username: user.username });
          get().clearAuth();
          throw new AuthorizationError();
        }

        set({
          user,
          accessToken: tokens.access_token,
          idToken: tokens.id_token,
          refreshToken: tokens.refresh_token,
          isAuthenticated: true,
          isLoading: false,
        });

        // Successful auth should clear any previous silent-SSO failure cooldown.
        if (typeof window !== 'undefined') {
          try {
            localStorage.removeItem('silent_sso_last_fail');
            // Reset the one-shot logout guard so a future stale-session
            // recovery can trigger again.
            sessionStorage.removeItem('token_exchange_session_cleared');
          } catch {
            // Ignore storage access errors.
          }
        }

        scheduleTokenRefresh(tokens.access_token);
        logger.debug('Auth tokens set successfully', { component: 'AuthStore', roles: user.roles });
      } catch (error) {
        if (error instanceof AuthorizationError) {
          // Session already cleared above; propagate so callers can surface an
          // "access denied" message rather than a generic failure.
          throw error;
        }
        logger.error('Failed to set tokens', { component: 'AuthStore', error: String(error) });
        telemetry.trackError('auth.set_tokens.failed', error);
        get().clearAuth();
        throw error;
      }
    },

    refreshTokens: async (): Promise<boolean> => {
      if (refreshPromise) {
        return refreshPromise;
      }

      const { refreshToken } = get();
      if (!refreshToken) {
        return false;
      }

      // Validate refresh token hasn't expired
      try {
        const refreshTokenExpiry = getTokenExpiration(refreshToken);
        if (!refreshTokenExpiry || refreshTokenExpiry.getTime() <= Date.now()) {
          logger.warn('Refresh token expired, clearing auth', { component: 'AuthStore' });
          telemetry.track('auth.refresh_token.expired');

          // Emit custom event for session expiration - App component will show modal
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('auth:session-expired'));
          }

          // Delay to allow modal to show before clearing auth
          setTimeout(() => {
            get().clearAuth();
          }, 100);
          return false;
        }
      } catch (error) {
        logger.error('Failed to validate refresh token', {
          component: 'AuthStore',
          error: String(error),
        });
        get().clearAuth();
        return false;
      }

      refreshPromise = (async () => {
        try {
          logger.debug('Attempting token refresh', { component: 'AuthStore' });
          const tokens = await authService.refreshToken(refreshToken);

          // Set tokens with validation
          await get().setTokens(tokens);

          // Fire-and-forget: re-run JIT provisioning so the DB record reflects
          // any profile or role changes that arrived with the refreshed token.
          const { accessToken: freshToken } = get();
          if (freshToken) {
            triggerJitProvisioning(freshToken);
          }

          logger.debug('Token refresh successful', { component: 'AuthStore' });
          return true;
        } catch (error) {
          logger.error('Token refresh failed', { component: 'AuthStore', error: String(error) });
          get().clearAuth();
          return false;
        } finally {
          refreshPromise = null;
        }
      })();

      return refreshPromise;
    },

    initializeAuth: async () => {
      // Prevent concurrent auth initialization using timestamp-based deduplication
      // This approach is more reliable than boolean flags and self-cleans after timeout
      const AUTH_INIT_TIMEOUT = 30000; // 30 seconds
      const SILENT_SSO_COOLDOWN = 300000; // 5 minutes - prevent infinite redirect loops
      const now = Date.now();
      const lastInitStr = sessionStorage.getItem('auth_init_timestamp');

      // Clean up expired localStorage entries on init (prevents accumulation)
      try {
        const lastSilentFailStr = localStorage.getItem('silent_sso_last_fail');
        if (lastSilentFailStr) {
          const lastFail = parseInt(lastSilentFailStr, 10);
          if (now - lastFail > SILENT_SSO_COOLDOWN) {
            localStorage.removeItem('silent_sso_last_fail');
          }
        }
      } catch (error) {
        // Ignore localStorage errors (may be disabled/full)
        logger.warn('Failed to clean up localStorage', {
          component: 'AuthStore',
          error: String(error),
        });
      }

      // Skip silent SSO on callback page.
      // This guard must run before init dedupe checks to avoid callback-page loading deadlocks.
      // Do NOT skip on /login: refresh may briefly route there before silent SSO completes.
      if (typeof window !== 'undefined') {
        const pathname = window.location.pathname;
        if (pathname === '/auth/callback') {
          set({ isLoading: false });
          return;
        }
      }

      if (lastInitStr) {
        const lastInit = parseInt(lastInitStr, 10);
        const timeSinceInit = now - lastInit;

        // If init was started recently, skip to prevent concurrent attempts
        if (timeSinceInit < AUTH_INIT_TIMEOUT) {
          const loginInProgress =
            sessionStorage.getItem('login_initiated') === 'true' ||
            Boolean(sessionStorage.getItem('token_exchange_in_progress'));

          logger.debug('Auth initialization already in progress or completed recently', {
            component: 'AuthStore',
            timeSinceInit,
            loginInProgress,
          });

          // Keep loading state while auth redirect/token exchange is in progress
          // to avoid flashing the login UI during page refresh.
          if (!loginInProgress) {
            set({ isLoading: false });
          }
          return;
        } else {
          // Clean up expired timestamp
          sessionStorage.removeItem('auth_init_timestamp');
        }
      }

      // Check if silent SSO was recently attempted and failed
      // Use localStorage to persist across tab/browser closes (prevents infinite redirect loops)
      const lastSilentFailStr = localStorage.getItem('silent_sso_last_fail');
      if (lastSilentFailStr) {
        const lastFail = parseInt(lastSilentFailStr, 10);
        if (now - lastFail < SILENT_SSO_COOLDOWN) {
          logger.debug('Silent SSO recently failed, skipping to prevent redirect loop', {
            component: 'AuthStore',
            timeSinceFail: now - lastFail,
          });
          set({ isLoading: false });
          return;
        }
      }

      // Attempt silent SSO on app load if not already authenticated
      const { isAuthenticated } = get();

      if (isAuthenticated) {
        set({ isLoading: false });
        return;
      }

      // Skip silent SSO in test mode (E2E testing)
      if (typeof window !== 'undefined' && sessionStorage.getItem('__test_mode__')) {
        logger.debug('Test mode detected, skipping silent SSO', { component: 'AuthStore' });
        set({ isLoading: false });
        return;
      }

      try {
        // Set timestamp to prevent concurrent auth attempts
        sessionStorage.setItem('auth_init_timestamp', now.toString());

        logger.debug('Attempting silent SSO', { component: 'AuthStore' });
        telemetry.track('auth.silent_sso.started');

        // Attempt silent SSO via a hidden iframe (prompt=none) rather than a
        // top-level redirect.  When no IdP session exists, Azure Workforce
        // (Entra ID) renders an AADSTS50058 interstitial instead of bouncing
        // back with error=login_required; running the flow inside an iframe
        // keeps that page invisible and simply resolves to null, so the user
        // lands on the login screen cleanly.  Staying in-page also preserves
        // the current route without needing intended_route round-tripping.
        const tokens = await authService.silentReauth();

        if (tokens) {
          await get().setTokens(tokens);

          // Fire-and-forget JIT provisioning so the DB record reflects any
          // profile/role changes carried by the fresh token.
          const { accessToken } = get();
          if (accessToken) {
            triggerJitProvisioning(accessToken);
          }

          logger.debug('Silent SSO successful', { component: 'AuthStore' });
          telemetry.track('auth.silent_sso.success');
          set({ isLoading: false });
        } else {
          // No active SSO session — show the login screen instead of
          // redirecting.  Record the failure to avoid retrying on every
          // navigation within the cooldown window.
          logger.debug('Silent SSO not available (no SSO session)', {
            component: 'AuthStore',
          });
          telemetry.track('auth.silent_sso.unavailable');
          localStorage.setItem('silent_sso_last_fail', Date.now().toString());
          set({ isLoading: false });
        }
      } catch (error) {
        if (error instanceof AuthorizationError) {
          // Silent SSO produced a valid token for an identity with no eQuip
          // access. The session is already cleared; record the cooldown so we
          // don't re-attempt on every load and land the user on the login
          // screen (interactive sign-in surfaces the access-denied message).
          logger.warn('Silent SSO denied: identity has no eQuip application role', {
            component: 'AuthStore',
          });
          telemetry.track('auth.silent_sso.unauthorized');
          localStorage.setItem('silent_sso_last_fail', Date.now().toString());
          set({ isLoading: false });
          return;
        }
        // Network or other unexpected error — don't mark as failed SSO so a
        // later attempt can retry; render the login screen for now.
        const errorMessage = error instanceof Error ? error.message : String(error);
        logger.warn('Silent SSO attempt failed due to network/other error', {
          component: 'AuthStore',
          error: errorMessage,
        });
        telemetry.track('auth.silent_sso.network_error', { error: errorMessage });
        set({ isLoading: false });
      }
    },
  };
});

// Tokens are kept in-memory only to minimize XSS exposure.
// Session restoration uses silent SSO (prompt=none) on app load.

// Expose store to window for E2E testing
if (typeof window !== 'undefined') {
  (window as TestWindow).useAuthStore = useAuthStore;
}
